import { getInputControlState } from 'ui/forms/input-control/input-control.helpers';
import {
  InputControlAsterisk,
  InputControlLabel,
  InputControlMessage,
  InputControlRoot,
  InputControlSupportiveText,
} from 'ui/forms/input-control/input-control.styles';
import type { InputControlProps } from 'ui/forms/input-control/input-control.types';

const InputControl = ({
  id,
  size,
  children,
  label,
  required,
  disabled,
  error,
  errorMessage,
  supportiveText,
  helperText,
  counterText,
  sx,
  'data-testid': dataTestId,
}: InputControlProps) => {
  const partTestId = (part: string) =>
    dataTestId ? `${dataTestId}-${part}` : undefined;

  const { isError, supportiveTextId, errorMessageId, helperTextId } =
    getInputControlState({
      id,
      disabled,
      error,
      errorMessage,
      supportiveText,
      helperText,
    });

  return (
    <InputControlRoot fullWidth disabled={disabled} error={isError} sx={sx}>
      {!!label && (
        <InputControlLabel htmlFor={id} fieldSize={size}>
          {label}
          {required && (
            <InputControlAsterisk
              aria-hidden="true"
              fieldSize={size}
              data-testid={partTestId('required-indicator')}
            >
              *
            </InputControlAsterisk>
          )}
        </InputControlLabel>
      )}
      {supportiveTextId && (
        <InputControlSupportiveText
          id={supportiveTextId}
          fieldSize={size}
          data-testid={partTestId('supportive-text')}
        >
          {supportiveText}
        </InputControlSupportiveText>
      )}
      {children}
      {errorMessageId && (
        <InputControlMessage
          id={errorMessageId}
          role="alert"
          data-testid={partTestId('error-message')}
        >
          {errorMessage}
        </InputControlMessage>
      )}
      {helperTextId && (
        <InputControlMessage
          id={helperTextId}
          error={false}
          data-testid={partTestId('helper-text')}
        >
          {helperText}
        </InputControlMessage>
      )}
      {!!counterText && (
        <InputControlMessage
          error={false}
          data-testid={partTestId('character-count')}
        >
          {counterText}
        </InputControlMessage>
      )}
    </InputControlRoot>
  );
};

export default InputControl;
